import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  approveTenant,
  cancelTenant,
  fetchTenant,
  impersonateTenant,
  reactivateTenant,
  rejectTenant,
  suspendTenant,
} from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import StatusBadge from './StatusBadge';
import RejectModal from './RejectModal';

const TenantDetailDrawer = ({ tenantId, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-tenant', tenantId],
    queryFn: () => fetchTenant(tenantId),
    enabled: !!tenantId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    queryClient.invalidateQueries({ queryKey: ['admin-tenant', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
    queryClient.invalidateQueries({ queryKey: ['admin-pending'] });
    queryClient.invalidateQueries({ queryKey: ['admin-activity'] });
  };

  const approveMut = useMutation({
    mutationFn: () => approveTenant(tenantId),
    onSuccess: () => {
      showToast(t('toast.tenantApproved'), 'success');
      invalidate();
    },
  });

  const rejectMut = useMutation({
    mutationFn: (reason) => rejectTenant(tenantId, reason),
    onSuccess: () => {
      showToast(t('toast.tenantRejected'), 'success');
      setRejectOpen(false);
      invalidate();
    },
  });

  const suspendMut = useMutation({
    mutationFn: (reason) => suspendTenant(tenantId, reason),
    onSuccess: () => {
      showToast(t('toast.tenantSuspended'), 'success');
      setSuspendOpen(false);
      invalidate();
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelTenant(tenantId),
    onSuccess: () => {
      showToast(t('toast.subscriptionCancelled'), 'success');
      invalidate();
    },
  });

  const reactivateMut = useMutation({
    mutationFn: () => reactivateTenant(tenantId),
    onSuccess: () => {
      showToast(t('toast.tenantReactivated'), 'success');
      invalidate();
    },
  });

  const impersonateMut = useMutation({
    mutationFn: () => impersonateTenant(tenantId),
    onSuccess: async (result) => {
      await startImpersonation(result.token, result.salonName);
      navigate('/salon/dashboard');
    },
  });

  if (!tenantId) return null;

  const tenant = data?.tenant;
  const stats = data?.stats;

  const statItems = stats
    ? [
        [t('admin.tenants.branchesStat'), stats.branchCount],
        [t('nav.staff'), stats.staffCount],
        [t('admin.tenants.bookingsStat'), stats.totalAppointments],
        [t('nav.orders'), stats.totalOrders],
        [t('admin.tenants.revenueStat'), `$${stats.totalRevenue.toLocaleString()}`],
      ]
    : [];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-lg flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 p-5">
          <h2 className="text-lg font-semibold">{t('admin.tenants.drawerTitle')}</h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
          {tenant && (
            <div className="space-y-6">
              {tenant.logo && (
                <img src={tenant.logo} alt="" className="h-16 w-16 rounded-lg object-cover" />
              )}
              <div>
                <h3 className="text-xl font-medium">{tenant.businessName}</h3>
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{tenant.ownerName}</p>
                <div className="mt-2 flex gap-2">
                  <StatusBadge status={tenant.subscriptionStatus} />
                  <span className="text-xs text-zinc-600 dark:text-zinc-500">{tenant.subscriptionPlan}</span>
                </div>
              </div>

              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-zinc-600 dark:text-zinc-500">{t('common.email')}</dt><dd>{tenant.email}</dd></div>
                <div className="flex justify-between"><dt className="text-zinc-600 dark:text-zinc-500">{t('common.phone')}</dt><dd>{tenant.phone || '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-zinc-600 dark:text-zinc-500">{t('common.address')}</dt><dd className="text-right">{tenant.address || '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-zinc-600 dark:text-zinc-500">{t('admin.tenants.requestedAt')}</dt><dd>{new Date(tenant.requestedAt).toLocaleDateString()}</dd></div>
              </dl>

              {stats && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {statItems.map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 p-3">
                      <p className="text-xs text-zinc-600 dark:text-zinc-500">{label}</p>
                      <p className="font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              )}

              {tenant.rejectionReason && (
                <p className="rounded-lg bg-red-500/10 p-3 text-sm text-red-300">
                  {t('admin.tenants.rejectedLabel', { reason: tenant.rejectionReason })}
                </p>
              )}
              {tenant.suspensionReason && (
                <p className="rounded-lg bg-orange-500/10 p-3 text-sm text-orange-300">
                  {t('admin.tenants.suspendedLabel', { reason: tenant.suspensionReason })}
                </p>
              )}
            </div>
          )}
        </div>

        {tenant && (
          <div className="space-y-2 border-t border-zinc-200 dark:border-zinc-800 p-5">
            {tenant.subscriptionStatus === 'PENDING' && (
              <>
                <button
                  type="button"
                  onClick={() => approveMut.mutate()}
                  className="w-full rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white hover:bg-emerald-500"
                >
                  {t('admin.tenants.approve')}
                </button>
                <button
                  type="button"
                  onClick={() => setRejectOpen(true)}
                  className="w-full rounded-lg border border-red-800 py-2 text-sm text-red-400 hover:bg-red-500/10"
                >
                  {t('admin.tenants.reject')}
                </button>
              </>
            )}
            {tenant.subscriptionStatus === 'ACTIVE' && (
              <>
                <button
                  type="button"
                  onClick={() => impersonateMut.mutate()}
                  className="w-full rounded-lg bg-glow-600 py-2 text-sm font-medium text-white hover:bg-glow-500"
                >
                  {t('admin.tenants.loginAsSalon')}
                </button>
                <button
                  type="button"
                  onClick={() => setSuspendOpen(true)}
                  className="w-full rounded-lg border border-orange-800 py-2 text-sm text-orange-400"
                >
                  {t('admin.tenants.suspend')}
                </button>
                <button
                  type="button"
                  onClick={() => cancelMut.mutate()}
                  className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm text-zinc-600 dark:text-zinc-400"
                >
                  {t('admin.tenants.cancelSubscription')}
                </button>
              </>
            )}
            {tenant.subscriptionStatus === 'SUSPENDED' && (
              <>
                <button
                  type="button"
                  onClick={() => reactivateMut.mutate()}
                  className="w-full rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white"
                >
                  {t('admin.tenants.reactivate')}
                </button>
                <button
                  type="button"
                  onClick={() => cancelMut.mutate()}
                  className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm text-zinc-600 dark:text-zinc-400"
                >
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>
        )}
      </aside>

      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={(reason) => rejectMut.mutate(reason)}
        loading={rejectMut.isPending}
      />

      {suspendOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <form
            className="w-full max-w-md rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6"
            onSubmit={(e) => {
              e.preventDefault();
              const reason = e.target.reason.value.trim();
              if (reason) suspendMut.mutate(reason);
            }}
          >
            <h3 className="text-lg font-medium">{t('admin.tenants.suspendModalTitle')}</h3>
            <textarea
              name="reason"
              rows={3}
              required
              className="mt-4 w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              placeholder={t('admin.tenants.suspendReasonPlaceholder')}
            />
            <div className="mt-4 flex justify-end gap-3">
              <button type="button" onClick={() => setSuspendOpen(false)} className="text-sm text-zinc-600 dark:text-zinc-400">
                {t('common.cancel')}
              </button>
              <button type="submit" className="rounded-lg bg-orange-600 px-4 py-2 text-sm text-white">
                {t('admin.tenants.suspend')}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default TenantDetailDrawer;
